const QUOTA_PENDING_STORAGE_KEY = "allstar-clip-quota-pending";
const QUOTA_USED_STORAGE_KEY = "allstar-clip-quota-used";
const PENDING_TTL_MS = 60 * 60 * 1000; // 1 h

export const addPendingClipCreation = (): void => {
  const pending = getValidTimestamps();
  pending.push(Date.now());
  localStorage.setItem(QUOTA_PENDING_STORAGE_KEY, JSON.stringify(pending));
};

export const getPendingClipCount = (): number => {
  return getValidTimestamps().length;
};

export const clearPendingClipCreations = (): void => {
  localStorage.removeItem(QUOTA_PENDING_STORAGE_KEY);
  localStorage.removeItem(QUOTA_USED_STORAGE_KEY);
};

/**
 * Reconcile pending clip creations against the backend-reported usage.
 * Called whenever we have fresh server data. Compares current server usage
 * against the last known server usage (persisted in localStorage so it
 * survives refreshes). Each unit of server increase removes one pending
 * entry, since the server has now counted that clip. Also prunes expired
 * entries.
 */
export const reconcilePendingClipCreations = (serverUsed: number): void => {
  const lastKnownUsed = getLastKnownServerUsed();
  const pending = getValidTimestamps();

  if (lastKnownUsed !== null && serverUsed > lastKnownUsed) {
    const increase = serverUsed - lastKnownUsed;
    const reconciled = pending.slice(Math.min(increase, pending.length));
    if (reconciled.length === 0) {
      localStorage.removeItem(QUOTA_PENDING_STORAGE_KEY);
    } else {
      localStorage.setItem(
        QUOTA_PENDING_STORAGE_KEY,
        JSON.stringify(reconciled),
      );
    }
  } else if (lastKnownUsed === null && pending.length > 0) {
    localStorage.removeItem(QUOTA_PENDING_STORAGE_KEY);
  } else {
    pruneExpired();
  }

  localStorage.setItem(QUOTA_USED_STORAGE_KEY, String(serverUsed));
};

const pruneExpired = (): void => {
  const valid = getValidTimestamps();
  if (valid.length === 0) {
    localStorage.removeItem(QUOTA_PENDING_STORAGE_KEY);
  } else {
    localStorage.setItem(QUOTA_PENDING_STORAGE_KEY, JSON.stringify(valid));
  }
};

const getLastKnownServerUsed = (): number | null => {
  try {
    const raw = localStorage.getItem(QUOTA_USED_STORAGE_KEY);
    if (raw === null) return null;
    const num = Number(raw);
    return Number.isFinite(num) ? num : null;
  } catch {
    return null;
  }
};

const getValidTimestamps = (): number[] => {
  const now = Date.now();
  return getStoredTimestamps().filter((ts) => now - ts < PENDING_TTL_MS);
};

const getStoredTimestamps = (): number[] => {
  try {
    const raw = localStorage.getItem(QUOTA_PENDING_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is number => typeof v === "number");
  } catch {
    return [];
  }
};
